import { ArrowRight, Check, CircleAlert, ListChecks, SlidersHorizontal, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { track } from "../../lib/analytics";
import { photoBackground } from "../../components/Cards";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatCalendarDate } from "../../lib/format";
import { useMyRequest } from "../../lib/queries";
import { REQUEST_STATUS_LABELS, REQUEST_STATUS_TONES, mxn, requestTimeline, reservationStatusCopy } from "../../lib/reservations";
import { RequestDetail } from "./RequestsPage";

const SHADE = "linear-gradient(180deg, rgba(0,37,87,0.15) 0%, rgba(0,37,87,0.9) 100%)";

/**
 * "Solicitud recibida" (A5, design 6a step 1): the request status page shown right after
 * submission, with the optional Apartado offer. Deferring keeps the request active and Wallet
 * Libre untouched (AC7). Later visits show the same page as the request's detail.
 */
export function RequestReceivedPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const justSubmitted = Boolean((location.state as { justSubmitted?: boolean } | null)?.justSubmitted);
  const detail = useMyRequest(id);
  usePageHeader(justSubmitted ? "Solicitud recibida" : "Mi solicitud", detail.data ? `Folio ${detail.data.request.folio}` : undefined);
  // Hooks stay above the early returns below. The offer is counted once per request when it shows.
  const offerShown = detail.data?.request.kind === "reservation" && detail.data.apartadoEligibility.eligible;
  useEffect(() => {
    if (offerShown && detail.data) track("apartado_offer_viewed", { request_id: detail.data.request.id, trip_id: detail.data.request.trip.id, minimum_amount: detail.data.apartadoEligibility.minimumContribution });
  }, [offerShown, detail.data?.request.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando solicitud" /><span>Cargando solicitud</span></div>;
  if (detail.isError || !detail.data) {
    return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos esta solicitud</strong><button className="secondary-button" onClick={() => navigate("/solicitudes")}>Ir a Mis solicitudes</button></div>;
  }
  const { request, apartadoEligibility: eligibility } = detail.data;
  const status = reservationStatusCopy(request.status);
  const timeline = requestTimeline(request);
  const showOffer = request.kind === "reservation" && eligibility.eligible;
  const minimum = eligibility.minimumContribution;

  return (
    <div className="page-container received-page">
      <div className="received-grid">
        <div className="received-main">
          <section className="card received-card">
            <span className="done-check"><Check size={24} strokeWidth={3} aria-hidden="true" /></span>
            <h2>{justSubmitted ? "Solicitud recibida" : REQUEST_STATUS_LABELS[request.status]}</h2>
            <p>
              {request.kind === "personalized"
                ? "Recibimos tu solicitud de viaje a la medida. Un especialista de Aventuria revisará tus preferencias y se pondrá en contacto contigo."
                : `Recibimos tu solicitud de reservación para ${request.trip.name}. Un asesor de Aventuria se pondrá en contacto contigo para confirmar disponibilidad, servicios y siguientes pasos.`}
            </p>
            <span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{status.label}</span>
            <dl className="received-facts">
              <div><dt>Folio</dt><dd translate="no">{request.folio}</dd></div>
              <div><dt>{request.departureDate ? "Fecha de salida" : "Fechas"}</dt><dd translate="no">{request.departureDate ? formatCalendarDate(request.departureDate, "long") : "Por definir"}</dd></div>
              <div><dt>Viajeros</dt><dd translate="no">{request.adults} {request.adults === 1 ? "adulto" : "adultos"}{request.children ? ` · ${request.children} ${request.children === 1 ? "niño" : "niños"}` : ""}{request.occupancyLabel ? ` · ${request.occupancyLabel}` : ""}</dd></div>
            </dl>
            {!request.apartado && request.kind === "reservation" && (
              <p className="received-note">Sin Apartado por ahora. Tu solicitud sigue su proceso; puedes crear el Apartado cuando quieras desde Mis solicitudes o tu Wallet.</p>
            )}
          </section>

          {showOffer ? (
            <section className="offer-card">
              <div className="offer-copy">
                <span className="offer-eyebrow"><Sparkles size={12} aria-hidden="true" /> Ahorra para tu viaje</span>
                <h3>Crea un Apartado para tu viaje</h3>
                <p>Empieza con <span translate="no">{mxn(minimum / Math.max(1, eligibility.travelers)).replace(".00", "")}</span> por pasajero, aporta cuando quieras y acércate a tu ritmo a {request.trip.name}.</p>
                <ul className="offer-perks">
                  <li><Check size={14} aria-hidden="true" /> Aporta a tu ritmo</li>
                  <li><Check size={14} aria-hidden="true" /> Sin pagos mensuales obligatorios</li>
                </ul>
                <div className="inline-actions">
                  <button type="button" className="primary-button" onClick={() => { track("apartado_offer_selected", { request_id: request.id, trip_id: request.trip.id }); navigate(`/solicitudes/${request.id}/apartado`); }}>Crear mi Apartado <ArrowRight size={16} aria-hidden="true" /></button>
                  <button type="button" className="link-button light" onClick={() => navigate(`/solicitudes?sel=${request.id}`)}>Quizá después</button>
                </div>
                <small>Completa el pago de tu viaje a través del Apartado 30 días antes de tu salida. Tu solicitud sigue su proceso, decidas lo que decidas.</small>
              </div>
              {eligibility.goalAmount !== null && (
                <div className="offer-goal">
                  <span>Tu meta de viaje</span>
                  <strong translate="no">{mxn(eligibility.goalAmount)}</strong>
                  <p>Esta meta contempla tu paquete actual. Puedes aumentarla para sumar vuelos, experiencias y otros servicios.</p>
                  {request.trip.slug && <button type="button" className="link-button light" onClick={() => navigate(`/viajes/${request.trip.slug}#incluye`)}><ListChecks size={14} aria-hidden="true" /> Ver qué incluye</button>}
                </div>
              )}
            </section>
          ) : request.apartado ? (
            <section className="card">
              <span className="page-eyebrow">Tu Apartado</span>
              <h3>Ya tienes un Apartado para este viaje</h3>
              <p translate="no">{mxn(request.apartado.balance)} apartados de una meta de {mxn(request.apartado.goalAmount)}.</p>
              <div className="inline-actions">
                <button type="button" className="primary-button solid" onClick={() => navigate(`/apartados/${request.apartado!.id}`)}>Ver apartado <ArrowRight size={16} aria-hidden="true" /></button>
                {request.trip.slug && <button type="button" className="link-button" onClick={() => navigate(`/viajes/${request.trip.slug}#incluye`)}><ListChecks size={14} aria-hidden="true" /> Ver qué incluye</button>}
                {request.apartado.status !== "closed" && <button type="button" className="link-button" onClick={() => navigate(`/apartados/${request.apartado!.id}?ajustar=1`)}><SlidersHorizontal size={14} aria-hidden="true" /> Ajustar mi meta</button>}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="received-rail">
          <div className="card received-trip">
            <div className="received-trip-photo" style={photoBackground(request.trip.photoUrl, SHADE, "half")}>
              <span className="hero-eyebrow">{request.trip.summary ?? (request.kind === "personalized" ? "Viaje a la medida" : "")}</span>
              <strong>{request.trip.name}</strong>
            </div>
            <div className="received-trip-body">
              <div className="label-row"><span className="stat-label" translate="no">Solicitud {request.folio}</span><span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{status.label}</span></div>
              <ol className="timeline compact">
                {timeline.map((step) => <li key={step.key} className={step.state}><span className="timeline-dot" aria-hidden="true" /><div><strong>{step.title}</strong><span>{step.detail}</span></div></li>)}
              </ol>
              <p className="resv-fine">Crear o abonar tu Apartado no confirma la reservación: tu asesor te escribe en cuanto los servicios estén garantizados.</p>
              <div className="inline-actions">
                {request.trip.slug && <button type="button" className="secondary-button small" onClick={() => navigate(`/viajes/${request.trip.slug}`)}>Ver viaje</button>}
                <button type="button" className="link-button" onClick={() => navigate(`/solicitudes?sel=${request.id}`)}>Todas mis solicitudes</button>
              </div>
            </div>
          </div>
          {!justSubmitted && <div className="card"><RequestDetail request={request} compact /></div>}
        </aside>
      </div>
    </div>
  );
}
